import { useState } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { UserCog, X } from 'lucide-react';
import cn from '../../utils/cn';
import { MAIN_NAV, ADMIN_NAV } from '../../routes/navigation';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { formatBytes, initials } from '../../utils/format';
import { P } from '../../utils/permissions';
import { FolderGlyph } from '../documents/FileTypeIcon';
import Menu, { MenuItem, MenuLabel } from '../common/Menu';
import authService from '../../services/authService';
import signatureService from '../../services/signatureService';
import useAsync from '../../hooks/useAsync';
import { USE_MOCK } from '../../services/apiClient';

/** "Amazon S3 · ap-south-1", or "This server's disk" — where the bytes actually sit. */
function storageLabel({ driver, region }) {
  if (driver === 's3') return `Amazon S3${region ? ` · ${region}` : ''}`;
  return "This server's disk";
}

function NavItem({ item, onNavigate, badge }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) => cn('navi', isActive && 'is-active')}
    >
      {item.icon && <item.icon size={18} strokeWidth={1.8} />}
      <span className="truncate" style={{ flex: 1 }}>{item.label}</span>
      {/* Only when there is something to do. A nav item wearing a permanent "0" is an item
          people stop reading. */}
      {badge > 0 && <span className="pill pill-accent tnum" style={{ fontSize: 11 }}>{badge}</span>}
    </NavLink>
  );
}

/**
 * Department entry with its top-level sub-units. Sub-units show under the active
 * department and the user's home department (v2 shell spec).
 */
function DepartmentItem({ department, subunits, isHome, onNavigate }) {
  const location = useLocation();
  const base = `/departments/${department.id}`;
  const isActive = location.pathname === base || location.pathname.startsWith(`${base}/`);
  const showChildren = subunits.length > 0 && (isActive || isHome);

  return (
    <div>
      <NavLink
        to={base}
        onClick={onNavigate}
        className={cn('navi', isActive && 'is-active', !department.isVisible && 'is-dim')}
        style={{ padding: '7px 10px' }}
        title={department.isVisible ? undefined : 'You do not have access to this department'}
      >
        <FolderGlyph size={18} />
        <span className="truncate" style={{ flex: 1 }}>
          {department.name}
        </span>
        <span className="count tnum">{department.documentCount?.toLocaleString?.() ?? ''}</span>
      </NavLink>
      {showChildren && (
        <div className="navi-sub">
          {subunits.map((sub) => (
            <NavLink
              key={sub.id}
              to={`${base}/${sub.id}`}
              onClick={onNavigate}
              className={({ isActive: subActive }) => cn('navi', subActive && 'is-active')}
            >
              <span className="truncate">{sub.name}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

/** Shared by the desktop sidebar and the mobile drawer. */
export function SidebarContent({ departments = [], subunitsByDept = {}, storage, onNavigate, compact = false }) {
  const { can, user, role, logout, switchUser } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(false);

  const adminItems = ADMIN_NAV.filter((i) => can(i.permission));
  const demoAccounts = USE_MOCK ? authService.demoAccounts() : [];
  const usedPct = storage ? Math.min(100, (storage.used / storage.total) * 100) : 0;

  // Counts a nav item can wear. Fetched here rather than on the page it links to, because a
  // queue nobody can see the size of from the outside is a queue nobody opens.
  const { data: toSign } = useAsync(() => signatureService.mine(), []);
  const badges = { toSign: toSign?.length || 0 };

  const doSwitch = async (id) => {
    setSwitching(true);
    try {
      await switchUser(id);
      navigate('/dashboard');
      onNavigate?.();
      toast.info('Viewing as a different role — navigation and actions now reflect it.');
    } finally {
      setSwitching(false);
    }
  };

  return (
    <>
      {/* Brand and navigation scroll together as one region; the footer below stays pinned. */}
      <div className="sidebar__nav">
        <Link to="/dashboard" className="sidebar__brand" onClick={onNavigate}>
          <span className="brand-mark" aria-hidden="true">
            iD
          </span>
          <span className="sidebar__brand-text">
            <b>Document Hub</b>
            <span>Inditech</span>
          </span>
        </Link>

        <div className="sidebar__group">
          {MAIN_NAV.map((item) => (
            <NavItem key={item.key} item={item} onNavigate={onNavigate} badge={badges[item.badge]} />
          ))}
        </div>

        <div className="eyebrow">Departments</div>
        <div className="sidebar__group" style={{ gap: 1 }}>
          {departments.map((d) => (
            <DepartmentItem
              key={d.id}
              department={d}
              subunits={compact ? [] : subunitsByDept[d.id] || []}
              isHome={d.id === user?.department?.id}
              onNavigate={onNavigate}
            />
          ))}
        </div>

        {adminItems.length > 0 && (
          <>
            <div className="eyebrow">Administration</div>
            <div className="sidebar__group" style={{ gap: 1 }}>
              {adminItems.map((item) => (
                <NavItem key={item.key} item={item} onNavigate={onNavigate} />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="sidebar__footer">
        {storage && (
          <div className="storage-card">
            <div className="storage-card__top">
              <b>{can(P.ADMIN_STORAGE_VIEW) ? 'Storage' : 'Your storage'}</b>
              <span className="muted tnum">{formatBytes(storage.usedBytes ?? storage.used)}</span>
            </div>

            {/* The bar is drawn only against an allowance somebody set and the server enforces.
                An earlier version filled toward a 5 TB total invented in a layout component and
                measured against nothing, which is why it was removed; `state` comes from the
                server so the colour here and the refusal on upload cannot disagree. With no
                allowance configured there is no denominator, and no bar. */}
            {storage.state && storage.state !== 'unmetered' && (
              <>
                <div className="storage-card__bar" role="presentation">
                  <span
                    className={cn(
                      'storage-card__fill',
                      storage.state === 'warning' && 'is-warning',
                      storage.state === 'full' && 'is-full',
                    )}
                    style={{ width: `${Math.min(100, Math.round(storage.fraction * 100))}%` }}
                  />
                </div>
                <div className="storage-card__where">
                  {storage.state === 'full'
                    ? `Full — ${formatBytes(storage.totalBytes)} used`
                    : `${Math.round(storage.fraction * 100)}% of ${formatBytes(storage.totalBytes)}`}
                </div>
              </>
            )}

            <div className="storage-card__where">{storageLabel(storage)}</div>
          </div>
        )}

        <Link to="/settings" className="sidebar__user" onClick={onNavigate}>
          <span className="avatar avatar--lg" aria-hidden="true">
            {initials(user?.name)}
          </span>
          <span style={{ minWidth: 0, flex: 1 }}>
            <span className="sidebar__user-name">{user?.name}</span>
            <span className="sidebar__user-role">
              {role?.name}
              {user?.department ? ` · ${user.department.name}` : ''}
            </span>
          </span>
        </Link>

        <div className="sidebar__links">
          {demoAccounts.length > 0 && (
            <Menu
              align="start"
              width={230}
              trigger={
                <button type="button" className="link" disabled={switching}>
                  View as…
                </button>
              }
            >
              <MenuLabel>Demo — view as role</MenuLabel>
              {demoAccounts.map((a) => (
                <MenuItem key={a.id} icon={UserCog} onClick={() => doSwitch(a.id)}>
                  {a.roleName}
                </MenuItem>
              ))}
            </Menu>
          )}
          <button
            type="button"
            className="link link--muted"
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </>
  );
}

export default function Sidebar(props) {
  return (
    <aside className="sidebar" aria-label="Main navigation">
      <SidebarContent {...props} />
    </aside>
  );
}

/** Mobile drawer over a scrim (< 860px). */
export function MobileDrawer({ open, onClose, ...props }) {
  if (!open) return null;
  return (
    <div className="drawer-scrim" onClick={onClose}>
      <nav className="drawer" onClick={(e) => e.stopPropagation()} aria-label="Navigation">
        <div className="row" style={{ justifyContent: 'flex-end', marginBottom: -8 }}>
          <button type="button" className="btn--icon" onClick={onClose} aria-label="Close navigation">
            <X size={18} />
          </button>
        </div>
        <SidebarContent {...props} onNavigate={onClose} compact />
      </nav>
    </div>
  );
}
